"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"

import {
  OrganizationSwitcher,
  useClerk,
  useOrganization,
  useUser,
} from "@clerk/nextjs"
import type { Theme } from "@clerk/ui/internal"
import {
  Button,
  cn,
  Divider,
  Popover,
  PopoverContent,
  PopoverItem,
  PopoverTrigger,
} from "@coval/ui"
import {
  BookOpen,
  Calendar,
  CircleUserRound,
  List,
  LoaderCircle,
  LogOut,
  MessageCircle,
  Settings,
} from "lucide-react"

import { useCollapsedSidebarSections } from "@/hooks/useCollapsedSidebarSections"
import { usePylonChat } from "@/hooks/usePylonChat"
import { useQueryParams } from "@/hooks/useQueryParams"
import { useSyncActiveOrganization } from "@/hooks/useSyncActiveOrganization"

import {
  useAppShellContributions,
  type AppShellNavigationItem,
} from "./AppShellContributionsContext"
import { AppSwitcher } from "./AppSwitcher"
import { SidebarNavItemRow } from "./SidebarNavItemRow"
import { getSidebarNavTestId } from "./sidebarNavTestIds"
import { SidebarSectionHeading } from "./SidebarSectionHeading"

type SidebarItem = AppShellNavigationItem

/**
 * Whether activating a link hands the destination to somewhere other than this
 * tab — Ctrl/Cmd- or Shift-click open it in a new tab or window, Alt-click
 * downloads it, and a non-primary button never navigates in place. The current
 * route is unchanged in every case, so a surface that closes on navigation
 * should stay put.
 */
const opensAwayFromThisPage = (event: React.MouseEvent) =>
  event.metaKey ||
  event.ctrlKey ||
  event.shiftKey ||
  event.altKey ||
  event.button !== 0

interface SidebarNavPanelProps {
  /** Width and any layout classes from the surface the panel is mounted in. */
  className?: string
  /**
   * Fires when a row the user activated would navigate. The docked panel
   * outlives the navigation and leaves this unset; the overlay uses it to
   * close itself behind the destination.
   */
  onNavigate?: () => void
}

const organizationSwitcherAppearance: Theme = {
  elements: {
    organizationSwitcherTrigger: {
      width: "100%",
      height: "100%",
      minHeight: "40px",
      padding: "0",
      backgroundColor: "transparent",
      border: "none",
      boxShadow: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
    },
    organizationSwitcherTriggerIcon: {
      marginLeft: "auto",
    },
    // Hide the "Create organization" action: ad-hoc org creation fires the
    // Clerk org.created webhook → auto-provisions a billing subscription,
    // which we're retiring. Org creation should flow only through the
    // controlled signup path. Switching between existing orgs is unaffected.
    organizationSwitcherPopoverActionButton__createOrganization: {
      display: "none",
    },
  },
}

/**
 * The primary navigation column: every section, row, and the account controls
 * beneath them. It is the same panel whether it is docked beside the page on a
 * wide viewport or carried in the overlay a narrow one opens, so the two never
 * drift apart — only the width and `onNavigate` differ.
 */
export const SidebarNavPanel = ({
  className,
  onNavigate,
}: SidebarNavPanelProps) => {
  const router = useRouter()
  const { organization } = useOrganization()
  const { user } = useUser()
  const appShellContributions = useAppShellContributions()
  // Which labelled sections the user has folded away, by item key — remembered
  // across navigations, reloads, and sessions.
  const { isCollapsed: isSectionCollapsed, toggle: toggleSection } =
    useCollapsedSidebarSections()

  const { signOut } = useClerk()
  const queryParams = useQueryParams()
  const { handleOpenChat } = usePylonChat()

  const { handleOrgSwitch } = useSyncActiveOrganization()

  const getHref = useCallback(
    (baseHref: string) => {
      return queryParams.getHref(baseHref, organization?.slug || undefined)
    },
    [queryParams, organization?.slug],
  )

  const site_config_items: SidebarItem[] =
    appShellContributions.primaryNavigationItems ?? []
  const adminItem = site_config_items.find((item) => item.key === "admin")

  const isActive = (href: string, baseHref?: string, matchExact?: boolean) => {
    // Check for exact match first
    if (router.asPath === href) {
      return true
    }

    // If baseHref is provided, check if current path starts with it
    // This makes parent items active when viewing child pages (e.g., test set detail page)
    if (baseHref && organization?.slug) {
      const currentPath = router.asPath.split(/[?#]/)[0]
      const hrefPath = href.split(/[?#]/)[0]

      // Treat /getting-started as part of the Home section
      if (baseHref === "/home" && currentPath === "/getting-started") {
        return true
      }

      // An entry that unfolds its child pages as their own rows is selected on
      // its own page only, so the parent and the child are never lit together.
      if (matchExact) {
        return currentPath === hrefPath
      }

      return currentPath.startsWith(hrefPath)
    }

    return false
  }

  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isAdminNavigating, setIsAdminNavigating] = useState(false)
  const [openPrimaryNavItemKey, setOpenPrimaryNavItemKey] = useState<
    string | null
  >(null)

  useEffect(() => {
    const handleAdminNavigationSettled = () => setIsAdminNavigating(false)

    router.events.on("routeChangeComplete", handleAdminNavigationSettled)
    router.events.on("routeChangeError", handleAdminNavigationSettled)

    return () => {
      router.events.off("routeChangeComplete", handleAdminNavigationSettled)
      router.events.off("routeChangeError", handleAdminNavigationSettled)
    }
  }, [router.events])

  const handleSignOut = () => {
    void signOut({ redirectUrl: "/signin" })
    setIsSettingsOpen(false)
  }

  // The overlay closes behind a row that navigates; a row whose own handler
  // takes over instead — Sofia's entry opens a surface in place — leaves it
  // open, since the user has not left the page. A modified or auxiliary click
  // opens the destination elsewhere and leaves this page standing, so it
  // leaves the overlay open too.
  const handleRowActivation = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (event.defaultPrevented || opensAwayFromThisPage(event)) {
        return
      }

      onNavigate?.()
    },
    [onNavigate],
  )

  const handleNavItemClick = useCallback(
    (item: SidebarItem) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      item.onClick?.(event)
      handleRowActivation(event)
    },
    [handleRowActivation],
  )

  const handleAdminItemClick = useCallback(
    (item: SidebarItem) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isAdminNavigating) {
        event.preventDefault()
        return
      }

      handleNavItemClick(item)(event)
      if (!event.defaultPrevented && !opensAwayFromThisPage(event)) {
        setIsAdminNavigating(true)
      }
    },
    [handleNavItemClick, isAdminNavigating],
  )

  const handleContactSupport = useCallback(() => {
    handleOpenChat()
    setIsSettingsOpen(false)
  }, [handleOpenChat])

  const organizationSwitcherRef = useRef<HTMLDivElement | null>(null)

  const handleOrgCardActivation = useCallback(
    (
      event:
        | React.MouseEvent<HTMLDivElement>
        | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      const triggerButton =
        organizationSwitcherRef.current?.querySelector<HTMLButtonElement>(
          ".cl-organizationSwitcherTrigger",
        )

      if (!triggerButton) {
        return
      }

      const target = event.target as HTMLElement | null
      if (target?.closest(".cl-organizationSwitcherTrigger")) {
        return
      }

      triggerButton.click()
    },
    [],
  )

  const handleOrgCardKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Enter" && event.key !== " ") {
        return
      }

      event.preventDefault()
      handleOrgCardActivation(event)
    },
    [handleOrgCardActivation],
  )

  // A module can also hang its own rows under its entry — navigation it owns
  // that isn't static config, unfolded while its section is the current one.
  const renderContributedNavItemChildren = (navItem: SidebarItem) =>
    appShellContributions.renderPrimaryNavItemChildren?.({ item: navItem })

  // Modules can own the row for their own nav entry (e.g. the dashboards
  // popover), whether that entry sits at the top level or inside a section.
  const renderContributedNavItem = (navItem: SidebarItem) =>
    appShellContributions.renderPrimaryNavItem?.({
      item: navItem,
      getHref,
      isActive,
      isOpen: openPrimaryNavItemKey === navItem.key,
      onOpenChange: (open) =>
        setOpenPrimaryNavItemKey(open ? navItem.key : null),
    })

  return (
    <div
      data-testid="sidebar"
      className={cn(
        "flex h-full min-h-0 flex-col bg-surface-contrast px-6 pt-5 pb-6",
        // A module unfolding its own navigation into the panel can widen it
        // for as long as those rows are shown; the transition carries the
        // content region beside it in step.
        "transition-[width] duration-200 ease-out",
        className,
      )}
    >
      {organization?.id === "org_2ztsBkYTQr8CLFR61h4d15y93kW" && (
        <div className="w-full px-2 mt-2 mb-5">
          <AppSwitcher />
        </div>
      )}

      {appShellContributions.renderSidebarPrimaryBeforeNav && (
        <div className="w-full mb-5">
          {appShellContributions.renderSidebarPrimaryBeforeNav({
            className: "w-full h-[28px]! max-h-[28px]!",
          })}
        </div>
      )}

      <div className="flex-1 overflow-y-auto w-full scrollbar-hide justify-start">
        <div className="flex flex-col gap-1 w-full">
          <nav className="flex flex-col gap-px">
            {site_config_items
              .filter((item) => item.key !== "settings" && item.key !== "admin")
              .map((item) => {
                const items = []

                if (item.subItems) {
                  const sectionExpanded = !isSectionCollapsed(item.key)

                  items.push(
                    <SidebarSectionHeading
                      key={item.key}
                      title={item.title}
                      expanded={sectionExpanded}
                      onToggle={() => toggleSection(item.key)}
                    />,
                  )

                  if (!sectionExpanded) {
                    return items
                  }

                  item.subItems.forEach((subItem) => {
                    const contributedSubItem = renderContributedNavItem(subItem)

                    if (contributedSubItem) {
                      items.push(
                        <React.Fragment key={subItem.key}>
                          {contributedSubItem}
                        </React.Fragment>,
                      )
                      return
                    }

                    items.push(
                      <SidebarNavItemRow
                        key={subItem.key}
                        href={subItem.href}
                        createHref={subItem.createHref}
                        createLabel={
                          subItem.createLabel ?? `New ${subItem.title}`
                        }
                        dataTestId={getSidebarNavTestId(subItem.href)}
                        onMouseEnter={() => setOpenPrimaryNavItemKey(null)}
                        onClick={handleNavItemClick(subItem)}
                        active={isActive(subItem.href, subItem.base_href)}
                      >
                        {React.createElement(subItem.icon || List, {
                          className: cn(
                            "w-4 h-4 shrink-0",
                            subItem.isFavorite
                              ? "text-blue-500"
                              : "text-current",
                          ),
                        })}
                        <span
                          className={cn(
                            "text-sm font-medium",
                            subItem.isFavorite ? "text-blue-500" : "",
                          )}
                        >
                          {subItem.title}
                        </span>
                      </SidebarNavItemRow>,
                    )
                  })
                } else {
                  const contributedItem = renderContributedNavItem(item)

                  if (contributedItem) {
                    items.push(
                      <React.Fragment key={item.key}>
                        {contributedItem}
                      </React.Fragment>,
                    )
                    return items
                  }

                  items.push(
                    <SidebarNavItemRow
                      key={item.key}
                      href={item.href}
                      createHref={item.createHref}
                      createLabel={item.createLabel ?? `New ${item.title}`}
                      dataTestId={getSidebarNavTestId(item.href)}
                      onMouseEnter={() => setOpenPrimaryNavItemKey(null)}
                      onClick={handleNavItemClick(item)}
                      active={isActive(
                        item.href,
                        item.base_href,
                        item.matchExact,
                      )}
                    >
                      {item.icon &&
                        React.createElement(item.icon, {
                          className: "w-4 h-4 text-current shrink-0",
                        })}
                      <span className="text-sm font-medium">{item.title}</span>
                    </SidebarNavItemRow>,
                  )

                  const contributedChildren =
                    renderContributedNavItemChildren(item)

                  if (contributedChildren) {
                    items.push(
                      <React.Fragment key={`${item.key}-children`}>
                        {contributedChildren}
                      </React.Fragment>,
                    )
                  }
                }

                return items
              })}
          </nav>
        </div>
      </div>

      <div className="mt-auto pt-4 flex flex-col gap-2">
        {appShellContributions.renderSidebarFooterBeforeOrg?.({
          orgSlug: organization?.slug ?? undefined,
          orgId: organization?.id,
        })}

        {organization && (
          <>
            {/* Custom organization switcher with hidden Clerk component

                    Docs: https://clerk.com/docs/components/organization/organization-switcher */}
            <div
              ref={organizationSwitcherRef}
              role="button"
              tabIndex={0}
              aria-label={`Switch organization, current organization is ${organization.name}`}
              className="flex items-center h-[28px] w-full cursor-pointer relative rounded-md border border-coval-border hover:bg-surface-0-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coval-info-border focus-visible:ring-offset-2"
              onClick={handleOrgCardActivation}
              onKeyDown={handleOrgCardKeyDown}
            >
              <div className="flex items-center gap-2 px-2 w-full pointer-events-none">
                {organization.imageUrl && (
                  <Image
                    src={organization.imageUrl}
                    alt={organization.name}
                    width={16}
                    height={16}
                    className="rounded shrink-0"
                  />
                )}
                <span className="text-sm text-coval-text truncate flex-1">
                  {organization.name}
                </span>
                <svg
                  fill="currentColor"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 16 16"
                  className="w-4 h-4 text-coval-text-muted shrink-0"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M4.24 6.2a.75.75 0 0 1 1.06.04l2.698 2.906 2.45-2.655a.744.744 0 0 1 .022-.021l.25-.25a.75.75 0 1 1 1.06 1.06l-.24.24-2.989 3.239a.75.75 0 0 1-1.1.001L4.2 7.26A.75.75 0 0 1 4.24 6.2Z"
                  />
                </svg>
              </div>
              {/* Hidden OrganizationSwitcher - opacity-0 hides trigger, modal still appears correctly */}
              <div className="absolute inset-0 opacity-0 z-10">
                {/* Switch-only: the create-organization action is hidden via
                    appearance (organizationSwitcherPopoverActionButton__createOrganization)
                    since ad-hoc org creation is retired — org creation flows
                    only through self-serve signup. */}
                <OrganizationSwitcher
                  hidePersonal
                  afterSelectOrganizationUrl={handleOrgSwitch}
                  appearance={organizationSwitcherAppearance}
                />
              </div>
            </div>
          </>
        )}

        {user?.primaryEmailAddress?.emailAddress && (
          <div className="flex w-full items-center gap-2">
            <Popover open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="secondary"
                  className="h-[28px] min-w-0 flex-1 justify-start items-center px-2 border border-coval-border hover:bg-surface-0-hover"
                >
                  <CircleUserRound className="w-4 h-4 text-coval-text-muted shrink-0" />
                  <span className="text-sm text-coval-text truncate flex-1 text-left">
                    {user.primaryEmailAddress.emailAddress}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-64 p-2 shadow-none"
                side="top"
                align="start"
              >
                <div className="flex flex-col gap-1">
                  <PopoverItem
                    onClick={() => {
                      void router.push(getHref("/settings"))
                      setIsSettingsOpen(false)
                      onNavigate?.()
                    }}
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </PopoverItem>

                  <PopoverItem
                    onClick={() => {
                      setIsSettingsOpen(false)
                      const w = window.open(
                        "https://docs.coval.ai",
                        "_blank",
                        "noopener,noreferrer",
                      )
                      if (w) {
                        w.opener = null
                      }
                    }}
                  >
                    <BookOpen className="w-4 h-4" />
                    Documentation
                  </PopoverItem>

                  <PopoverItem
                    onClick={() => {
                      setIsSettingsOpen(false)
                      const w = window.open(
                        "https://cal.com/bnhopkins/demo",
                        "_blank",
                        "noopener,noreferrer",
                      )
                      if (w) {
                        w.opener = null
                      }
                    }}
                  >
                    <Calendar className="w-4 h-4" />
                    Book a Demo
                  </PopoverItem>

                  <PopoverItem onClick={handleContactSupport}>
                    <MessageCircle className="w-4 h-4" />
                    Contact Us
                  </PopoverItem>

                  <Divider />

                  <PopoverItem
                    className="text-coval-danger-text"
                    onClick={handleSignOut}
                  >
                    <LogOut className="w-4 h-4" />
                    Sign Out
                  </PopoverItem>
                </div>
              </PopoverContent>
            </Popover>

            {adminItem && (
              <Button
                asChild
                icon
                tooltip={isAdminNavigating ? "Opening Admin…" : "Admin"}
                tooltipProps={{ side: "top" }}
                variant="secondary"
                active={isActive(adminItem.href, adminItem.base_href)}
                className={cn(
                  "h-[28px] w-[28px] shrink-0 border border-coval-border hover:bg-surface-0-hover",
                  isAdminNavigating && "cursor-wait",
                )}
              >
                <Link
                  href={adminItem.href}
                  aria-busy={isAdminNavigating}
                  aria-disabled={isAdminNavigating}
                  aria-label={isAdminNavigating ? "Opening Admin" : "Admin"}
                  onClick={handleAdminItemClick(adminItem)}
                >
                  {isAdminNavigating ? (
                    <LoaderCircle
                      aria-hidden="true"
                      className="size-4 animate-spin text-current"
                    />
                  ) : (
                    adminItem.icon &&
                    React.createElement(adminItem.icon, {
                      className: "size-4 text-current",
                    })
                  )}
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
